// Cadastro de gente nova na equipe (2026-09-28).
//
// Até aqui a tela só EDITAVA quem já existia: profissional novo só nascia por
// INSERT no banco a mão (o sync do Conta Azul vincula mas não cria, e o import
// do Notion bloqueia quando não acha). Este modal é a porta de entrada.
//
// O formulário pede o MÍNIMO que faz a pessoa aparecer certa na lista da
// Equipe (nome, login, cargo, admissão) e manda direto para a ficha dela — é
// lá que entram CPF, endereço, documentos e férias, sem transformar o
// cadastro numa parede de campos.
//
// 🔒 Remuneração NÃO entra aqui: salário e coeficiente têm permissão própria
//    (financeiro.profissionais.*) e vivem na aba Remuneração da ficha.
import { ReactElement, useState } from 'react';
import {
  Alert, DatePicker, Flex, Input, InputNumber, Modal, Typography,
  App as AntApp,
} from 'antd';
import dayjs from 'dayjs';
import { acaoRH } from './FichaProfissional';

const { Text } = Typography;

export default function NovoProfissional({ usuariosSistema, onFechar, aoCriar }: {
  /** Logins do sistema, para sugerir o e-mail (mesma fonte da ficha). */
  usuariosSistema: { email: string; nome: string }[];
  onFechar: () => void;
  /** Recebe o id de quem nasceu — a Equipe recarrega e abre a ficha. */
  aoCriar: (id: string, aviso?: string) => void;
}): ReactElement {
  const { message } = AntApp.useApp();
  // Desde o ciclo da pessoa (28/09/2026) o login NÃO se digita aqui: ligar um
  // e-mail exige conferir a conta, gravar a identidade e religar as horas — é
  // "Nova pessoa" ou "Vincular login" na ficha, só pelo administrador.
  const [f, setF] = useState({
    nome: '', cargo: '', admissao: '', horas_dia: 8 as number | null,
  });
  const [salvando, setSalvando] = useState(false);
  const set = (k: string, v: any) => setF((p) => ({ ...p, [k]: v }));
  void usuariosSistema;

  async function salvar() {
    const nome = f.nome.trim();
    if (nome.length < 3) { message.warning('Escreva o nome completo da pessoa'); return; }
    setSalvando(true);
    try {
      const r = await acaoRH<{ id: string; nome: string; aviso?: string }>('rh_pessoa_criar', {
        nome,
        cargo: f.cargo.trim() || undefined,
        admissao: f.admissao || undefined,
        horas_dia: f.horas_dia ?? undefined,
      });
      message.success(`${r.nome} entrou na equipe.`);
      aoCriar(r.id, r.aviso);
    } catch (e: any) {
      // 409 (nome ou e-mail repetido) chega com a frase pronta do servidor,
      // que já diz o que fazer — mostrar por mais tempo, é texto de decisão.
      const texto = e?.response?.data?.error || e.message;
      if (e?.response?.status === 409) message.warning({ content: texto, duration: 8 });
      else message.error('Não foi possível cadastrar: ' + texto);
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={() => !salvando && onFechar()} onOk={salvar}
      confirmLoading={salvando} title="Novo profissional (sem login)"
      okText="Cadastrar" cancelText="Cancelar"
      width="94vw" style={{ maxWidth: 560, top: 24 }}
      styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      destroyOnClose>
      <Flex vertical gap={12} style={{ marginTop: 8 }}>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Nome completo</Text>
          <Input autoFocus value={f.nome} onChange={(e) => set('nome', e.target.value)}
            onPressEnter={() => !salvando && salvar()}
            placeholder="Como aparece no contrato e nas notas de pagamento" />
        </div>

        <Alert type="info" showIcon style={{ fontSize: 12 }}
          message="Ficha sem login: para quem não vai entrar no sistema (freelancer, autônomo)."
          description='Quem vai anotar horas no sistema entra por "Nova pessoa" (login + ficha juntos, só o administrador). Se esta pessoa ganhar login depois, use "Vincular login" na ficha.' />

        <Flex gap={12} wrap>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Cargo (opcional)</Text>
            <Input value={f.cargo} onChange={(e) => set('cargo', e.target.value)}
              placeholder="ex: Topógrafo, Engenheira Ambiental" />
          </div>
          <div style={{ flex: 1, minWidth: 170 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Admissão (opcional)</Text>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
              value={f.admissao ? dayjs(f.admissao) : null}
              onChange={(d) => set('admissao', d ? d.format('YYYY-MM-DD') : '')} />
          </div>
        </Flex>

        <div style={{ maxWidth: 200 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Horas por dia</Text>
          <InputNumber style={{ width: '100%' }} min={0} max={24} step={1}
            value={f.horas_dia} onChange={(v) => set('horas_dia', v)} />
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
            Jornada esperada — base da cobertura de horas. Padrão 8.
          </div>
        </div>

        <Alert type="info" showIcon
          message="CPF, endereço, contato de emergência, documentos e férias entram na ficha, que abre logo depois de cadastrar."
          style={{ fontSize: 12 }} />
      </Flex>
    </Modal>
  );
}
